/**
 * V17.523 (Phase 1): state of the one menu for the signed-in user — visible sections, active
 * item, which sections are open, and the collapsed rail — with Rule 4's first-visit rule and
 * persistence in localStorage `vtw.nav.v1.<userId>`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import type { NavSectionId } from '../../app/navigation';
import { activeItemPath, firstVisitOpen, loadNavState, saveNavState, visibleSections } from './navModel';
import { useNavUser } from './useNavUser';

export function useNavMenu() {
  const { user, getDefaultRoute } = useAuth();
  const navUser = useNavUser();
  const { isDesktopSidebarOpen, setDesktopSidebarOpen } = useLayout();
  const location = useLocation();
  const userId = user?.id ?? 'anon';

  const sections = useMemo(() => visibleSections(navUser), [navUser]);
  const items = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const activePath = activeItemPath(location.pathname, location.hash, items);
  // V17.525 (row 87, ruling pm/2026-10-26-049 Q3): Home goes to the user's own landing page —
  // resolveDefaultRoute, the same resolver login uses — not the literal /dashboard, which refuses
  // partners, scouts and field officers. It lights when the user is on that page.
  const homePath = getDefaultRoute();
  const homeActive = location.pathname === homePath;
  const activeSection: NavSectionId | null = useMemo(
    () => sections.find((s) => s.items.some((i) => i.path === activePath))?.id ?? null,
    [sections, activePath],
  );

  const [open, setOpen] = useState<Set<string>>(() => {
    const saved = loadNavState(userId);
    return new Set(saved ? saved.open : firstVisitOpen(sections, activeSection, navUser));
  });

  // The saved rail state is applied through LayoutContext, which lands one render later. Until it
  // has landed nothing is saved — otherwise the not-yet-restored default overwrites it (React
  // StrictMode runs effects twice in development, which made that race visible).
  const pendingCollapsed = useRef<boolean | null>(loadNavState(userId)?.collapsed ?? null);

  // A different user signs in on this browser → their own saved state, or their first visit.
  const loadedFor = useRef(userId);
  useEffect(() => {
    if (loadedFor.current === userId) return;
    loadedFor.current = userId;
    const saved = loadNavState(userId);
    setOpen(new Set(saved ? saved.open : firstVisitOpen(sections, activeSection, navUser)));
    pendingCollapsed.current = saved ? saved.collapsed : null;
    if (saved) setDesktopSidebarOpen(!saved.collapsed);
  }, [userId, sections, activeSection, navUser, setDesktopSidebarOpen]);

  // Restore the saved rail state.
  useEffect(() => {
    if (pendingCollapsed.current !== null) setDesktopSidebarOpen(!pendingCollapsed.current);
  }, [setDesktopSidebarOpen]);

  useEffect(() => {
    if (pendingCollapsed.current !== null) {
      if (!isDesktopSidebarOpen !== pendingCollapsed.current) return; // restore not applied yet
      pendingCollapsed.current = null;
    }
    saveNavState(userId, { collapsed: !isDesktopSidebarOpen, open: [...open] });
  }, [userId, isDesktopSidebarOpen, open]);

  const toggleSection = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allOpen = sections.every((s) => open.has(s.id));
  const toggleAll = useCallback(() => {
    setOpen(allOpen ? new Set() : new Set(sections.map((s) => s.id)));
  }, [allOpen, sections]);

  return {
    homePath,
    homeActive,
    sections,
    items,
    activePath,
    activeSection,
    open,
    toggleSection,
    allOpen,
    toggleAll,
    /** Rule 4: "Collapse all / Expand all" only when more than 3 sections are visible. */
    showToggleAll: sections.length > 3,
  };
}
